import { useEffect, useRef, useCallback, useState } from "react";
import { WS_HEARTBEAT_INTERVAL_MS, WS_HEARTBEAT_TIMEOUT_MS, WS_RECONNECT_BASE_MS, WS_RECONNECT_MAX_MS } from "@/constants/game";

export function useGameSocket(gameId, role, onMessage) {
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const deadCheckRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [homeConnected, setHomeConnected] = useState(false);
  const [awayConnected, setAwayConnected] = useState(false);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log(`[WS] send ${data.type}`);
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!gameId || !role) return;

    const base = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001")
      .replace(/^http/, "ws");
    const url = `${base}/ws/${gameId}/${role}`;

    // Closure-scoped flags so stale events from a previous mount's WS can't toggle state.
    // Critical for React StrictMode dev double-mount and for clean disconnect handling.
    let cancelled = false;
    let attempt = 0;

    const clearHeartbeat = () => {
      clearInterval(pingIntervalRef.current);
      clearInterval(deadCheckRef.current);
      pingIntervalRef.current = null;
      deadCheckRef.current = null;
    };

    const connect = () => {
      if (cancelled) return;
      // ── Single-flight ────────────────────────────────────────────────────
      // connect() is reachable from three places: effect start, the
      // visibilitychange handler, and the backoff timer in onclose. None of them
      // checked whether a socket was already live, so a race opened a SECOND one
      // and simply overwrote wsRef — leaving the first OPEN and unreferenced.
      //
      // That is the head-to-head desync. The client's message handlers correctly
      // ignore an orphan (they compare wsRef.current !== ws), but the SERVER does
      // not: it keeps its own per-role binding, so a reply addressed via send_to
      // could go to the socket the client had abandoned. Hence lock-ins that were
      // sent, accepted, and never answered — and duplicate delivery of frames the
      // reducer then applied twice, driving the phase into a state with no exit.
      //
      // An audit measured 2-3 `connecting attempt=0` lines in the same second on
      // nearly every mount. After this, exactly one per mount is the regression
      // test — observable before any gameplay.
      const live = wsRef.current;
      if (live && (live.readyState === WebSocket.CONNECTING || live.readyState === WebSocket.OPEN)) {
        console.log(`[WS] connect() skipped — socket already ${live.readyState === WebSocket.OPEN ? "OPEN" : "CONNECTING"}`);
        return;
      }
      // Belt and braces: if a previous socket is mid-close, drop our reference to it
      // so it can never be mistaken for the live one.
      if (live) {
        try { live.close(1000, "superseded"); } catch {}
      }
      console.log(`[WS] connecting attempt=${attempt} url=${url}`);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      // Per-connection timestamp; updated by incoming pong messages.
      let lastPongAt = Date.now();

      ws.onopen = () => {
        if (cancelled || wsRef.current !== ws) return;
        console.log(`[WS] connected (attempt=${attempt})`);
        attempt = 0;
        setConnected(true);
        setReconnecting(false);
        clearTimeout(reconnectRef.current);

        // Explicit state-sync ping. The server pushes a `game_state` automatically
        // on every connect, but during a flaky / fast reconnect that initial
        // send_to can fire while the socket is still transitional and silently
        // drop. Without this safety net the client lands "connected" but with
        // stale `game.state` until the server's next mutation broadcast — which,
        // mid-sim, can leave one client a play behind (missing runners, wrong
        // batter, off count). request_state is rate-limit-exempt and idempotent
        // so the redundancy is cheap.
        try { ws.send(JSON.stringify({ type: "request_state" })); } catch {}

        // ── Heartbeat ────────────────────────────────────────────────────────
        // Send a ping every 15 s. If no pong arrives within 30 s the TCP
        // connection is silently dead (e.g. mobile going to sleep, NAT timeout).
        // Force-close so the exponential-backoff reconnect fires immediately
        // rather than waiting for the OS-level TCP timeout (~2 min).
        clearHeartbeat(); // clear any stale timers from prior connection
        lastPongAt = Date.now();

        pingIntervalRef.current = setInterval(() => {
          if (!cancelled && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: "ping", ts: Date.now() })); } catch {}
          }
        }, WS_HEARTBEAT_INTERVAL_MS);

        deadCheckRef.current = setInterval(() => {
          if (!cancelled && Date.now() - lastPongAt > WS_HEARTBEAT_TIMEOUT_MS) {
            console.warn("[WS] heartbeat timeout — forcing reconnect");
            try { ws.close(4001, "heartbeat_timeout"); } catch {}
          }
        }, WS_RECONNECT_BASE_MS * 20);
      };

      ws.onmessage = (e) => {
        if (cancelled || wsRef.current !== ws) return;
        try {
          const data = JSON.parse(e.data);
          if (!data || typeof data !== "object" || typeof data.type !== "string") {
            console.warn("[useGameSocket] malformed WS message (missing type):", e.data?.slice?.(0, 120));
            return;
          }

          // Heartbeat: reset the dead-connection clock; don't forward to game logic.
          if (data.type === "pong") {
            lastPongAt = Date.now();
            return;
          }

          if (data.type === "presence") {
            setHomeConnected(data.home_connected);
            setAwayConnected(data.away_connected);
          }
          onMessageRef.current?.(data);
        } catch (e) {
          console.warn("[useGameSocket] failed to parse WS message", e);
        }
      };

      ws.onclose = (event) => {
        console.log(`[WS] closed code=${event?.code} reason=${event?.reason} cancelled=${cancelled}`);
        clearHeartbeat();
        if (cancelled) return; // Cleanup unmount — don't bounce-reconnect.
        if (wsRef.current !== ws) return; // Stale WS already replaced — ignore.
        setConnected(false);
        // Server kicked us because another window/tab took over this role. Don't reconnect —
        // otherwise the two clients ping-pong forever, each booting the other.
        if (event && event.code === 4000) {
          cancelled = true;
          return;
        }
        // Exponential backoff capped at ~5s. First retry is fast (250ms) so brief drops
        // don't show as "Reconnecting…" for long.
        const delay = Math.min(WS_RECONNECT_BASE_MS * Math.pow(2, attempt), WS_RECONNECT_MAX_MS);
        attempt += 1;
        setReconnecting(true);
        reconnectRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    };

    connect();

    // Page Visibility API: reconnect immediately when the tab becomes visible
    // instead of waiting up to 65s for the heartbeat-timeout check.
    // Browsers throttle background-tab timers, which can let the server's
    // protocol-level ping expire before our JS can respond. Forcing a
    // reconnect on focus means the user never sees a stale disconnected state.
    const handleVisibilityChange = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      const ws = wsRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        console.log("[WS] tab visible — dead connection, reconnecting immediately");
        clearTimeout(reconnectRef.current);
        connect();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(reconnectRef.current);
      clearHeartbeat();
      const ws = wsRef.current;
      wsRef.current = null;
      // Explicit code: an unmount close should be distinguishable from a network
      // drop (1006) or a server kick (4000) when reading the console.
      try { ws?.close(1000, "unmount"); } catch {}
    };
  }, [gameId, role]);

  return { send, connected, reconnecting, homeConnected, awayConnected };
}
